import type { ComboboxItem } from '@/components'
import { Combobox, UserCombobox } from '@/components'
import { Checkbox } from '@/components_deprecated'
import { PlainLink } from '@/components_deprecated/Link'
import { cn } from '@/lib/utils'
import type { ThumbnailUser } from '@/modules/users/UserThumbOrInitials'
import { format } from '@/utils/date-fns'
import { usePermissions } from '@/utils/hooks'
import { unlessLoadingDots } from '@/utils/loading'
import { PERMISSIONS } from '@/utils/permissions'
import { overloadColor } from '@/utils/style'
import type {
  FormErrors,
  FormOnChangeFn,
  UpdateFormFn,
} from '@ur-solutions/react-hooks'
import { useTranslate } from '@ur-solutions/react-hooks'
import React, { useEffect, useEffectEvent, useMemo, useState } from 'react'
import styled from 'styled-components'
import { useIssueSeverityOptions } from '../consts'
import type { EditIssueForm } from '../types'
import type {
  IssueCategoryNode,
  IssueRiskAspectNode,
  IssueSeverity,
  LocalSubcategory,
} from '../types.graphl'
import type { ShallowCategory } from './CategorySelect'
import { CategorySelect } from './CategorySelect'
import { IssueDateRangeField, isSingleDay } from './issue-date-range-field'
import { IssueRiskAspectCombobox } from './risk-aspect-combobox'
import { useIssueProcessField } from './use-issue-process-field'

const Card = styled.section`
  border-radius: 16px;
  background: white;
  box-shadow: var(--shadow-card);

  /* On mobile the columns flatten (see the view's Content rule); lift this
     metadata card to sit directly after the title. */
  ${props => props.theme.media.custom({ max: '1100px' })} {
    order: -1;
  }
`

const PILL =
  'inline-flex items-center gap-2 self-start rounded-full border px-3 py-1 text-xs font-semibold'
const TONE = {
  emerald: 'border-emerald-300 bg-emerald-100 text-emerald-900',
  amber: 'border-amber-300 bg-amber-100 text-amber-900',
  rose: 'border-rose-300 bg-rose-100 text-rose-900',
  stone: 'border-stone-300 bg-stone-100 text-stone-800',
} as const

const Row: React.FC<
  React.PropsWithChildren<{ label: React.ReactNode; required?: boolean }>
> = ({ label, required, children }) => (
  <div className="space-y-1.5 border-t border-stone-200 px-5 py-3.5 first:border-t-0">
    <div className="flex items-center gap-1 text-xs font-semibold uppercase text-stone-500">
      {label}
      {required && <span className="text-rose-500">*</span>}
    </div>
    <div className="text-sm font-medium text-stone-900">{children}</div>
  </div>
)

interface MainInfoProps {
  loading: boolean
  editing: boolean
  form: EditIssueForm
  errors: FormErrors<EditIssueForm>
  subCategory?: LocalSubcategory
  category:
    | (Pick<IssueCategoryNode, 'id' | 'name' | 'color'> & {
        responsibleUser: ThumbnailUser | null
      })
    | null
    | undefined
  date: string | undefined
  closedAt: string | null | undefined
  archivedAt: string | null | undefined
  reference: string | null | undefined
  user: ThumbnailUser | null | undefined
  responsible: ThumbnailUser | null | undefined
  caseHandler: ThumbnailUser | null | undefined
  severity: IssueSeverity | undefined
  riskAspect: IssueRiskAspectNode | null | undefined
  dateEnd: string | undefined

  requireSeverity: boolean
  requireRiskAspect: boolean

  onChange: FormOnChangeFn<EditIssueForm>
  updateForm: UpdateFormFn<EditIssueForm>
  subcategoryOptions: ComboboxItem<string>[]
  subcategoriesLoading: boolean
}

export const MainInfo: React.FC<MainInfoProps> = ({
  loading,
  editing,
  form,
  errors,
  subCategory,
  category,
  date,
  closedAt,
  archivedAt,
  reference,
  user,
  responsible,
  caseHandler,
  severity,
  riskAspect,
  dateEnd,
  requireSeverity,
  requireRiskAspect,

  onChange,
  updateForm,
  subcategoryOptions,
  subcategoriesLoading,
}) => {
  const translations = useTranslate({
    unknown: 'common.unknown',

    open: 'common.open-alt',
    closed: 'common.closed',
    archived: 'common.archived',

    status: 'common.status',
    subCategory: 'common.sub-category',
    category: 'common.category',
    reference: ['issues.reference', { abbreviate: false }],
    user: 'common.submitter',
    responsible: 'common.responsible',
    caseHandler: 'common.case-handler',
    date: 'common.date',

    riskAspect: 'common.risk-aspect',
    process: 'issues.process',

    none: 'common.none',
    severity: 'common.severity',
    severityGrades: {
      LOW: 'common.low',
      MEDIUM: 'common.medium',
      HIGH: 'common.high',
    },
  })

  const severityOptions = useIssueSeverityOptions()
  const processField = useIssueProcessField(form.processChoice)

  const [temporaryResponsible, setTemporaryResponsible] = useState<{
    id: string
  } | null>(null)

  const { hasPermissions } = usePermissions()
  const canEdit = hasPermissions(PERMISSIONS.issues.change.issue)
  const showEditMark = editing && canEdit

  const handleSetTemporaryResponsible = useEffectEvent(
    (user: { id: string } | null) => {
      setTemporaryResponsible(user)
    }
  )

  useEffect(() => {
    handleSetTemporaryResponsible(editing ? responsible ?? null : null)
  }, [editing, responsible])

  function handleChangeCategory(id: string, category: ShallowCategory | null) {
    updateForm({
      category: id,
      severity: category?.defaultSeverity ?? form.severity,
    })
    setTemporaryResponsible(category?.responsibleUser ?? temporaryResponsible)
  }

  const unlessLoading = unlessLoadingDots(loading, translations.unknown)

  const issueStatus = useMemo(() => {
    if (typeof closedAt === 'undefined' && typeof archivedAt === 'undefined')
      return null

    const archived =
      archivedAt !== null ? ` (${translations.archived.toLowerCase()})` : ''
    const tone =
      archivedAt !== null ? 'stone' : closedAt !== null ? 'emerald' : 'amber'
    const text =
      closedAt !== null
        ? `${translations.closed}${archived}`
        : `${translations.open}${archived}`

    return (
      <span className={cn(PILL, TONE[tone])}>
        <span className="h-1.5 w-1.5 rounded-full bg-current" />
        {text}
      </span>
    )
  }, [
    archivedAt,
    closedAt,
    translations.archived,
    translations.closed,
    translations.open,
  ])

  const displaySeverity = severity ?? form.severity

  useEffect(() => {
    if (subcategoryOptions?.length > 0 || subcategoriesLoading) return

    if (form.subcategory !== null) {
      onChange('subcategory')(null)
    }
  }, [
    form.category,
    subcategoryOptions,
    subcategoriesLoading,
    form.subcategory,
    onChange,
  ])

  return (
    <Card>
      <Row label={translations.status}>
        {!editing ? (
          unlessLoading(issueStatus)
        ) : (
          <Checkbox
            checked={form.closed}
            label={translations.closed}
            boxSize="2rem"
            onChange={onChange('closed')}
          />
        )}
      </Row>

      <Row
        label={translations.severity}
        required={editing && canEdit && requireSeverity}
      >
        {!editing || !canEdit ? (
          displaySeverity ? (
            <span
              className={cn(
                PILL,
                TONE[
                  displaySeverity === 'LOW'
                    ? 'emerald'
                    : displaySeverity === 'MEDIUM'
                    ? 'amber'
                    : 'rose'
                ]
              )}
            >
              <span className="h-1.5 w-1.5 rounded-full bg-current" />
              {unlessLoading(translations.severityGrades[displaySeverity])}
            </span>
          ) : (
            unlessLoading('-')
          )
        ) : (
          <div>
            <Combobox<string>
              mode="single"
              value={form.severity ?? ''}
              items={severityOptions}
              clearable
              onChange={value =>
                onChange('severity')((value || null) as IssueSeverity | null)
              }
            />
            {errors.severity && (
              <p className="mt-1 text-xs text-rose-500">{errors.severity}</p>
            )}
          </div>
        )}
      </Row>

      <Row label={translations.category} required={showEditMark}>
        {!editing || !canEdit ? (
          category ? (
            <span className={cn(PILL, TONE.stone)}>
              <span
                className="h-1.5 w-1.5 rounded-full"
                style={{ background: overloadColor(category.color) }}
              />
              {unlessLoading(category.name)}
            </span>
          ) : (
            unlessLoading('-')
          )
        ) : (
          <CategorySelect
            value={form.category}
            onChange={handleChangeCategory}
          />
        )}
        {!editing || !canEdit
          ? subCategory?.name && (
              <p className="mt-2 text-xs text-stone-500">{subCategory.name}</p>
            )
          : form.category &&
            subcategoryOptions?.length > 0 && (
              <div className="mt-2">
                <div className="mb-1 flex items-center gap-1 text-xs font-semibold uppercase text-stone-500">
                  {translations.subCategory}
                  {showEditMark && <span className="text-rose-500">*</span>}
                </div>
                <Combobox<string>
                  mode="single"
                  value={form.subcategory ?? ''}
                  items={subcategoryOptions}
                  clearable
                  onChange={value => onChange('subcategory')(value || null)}
                />
                {errors.subcategory && (
                  <p className="mt-1 text-xs text-rose-500">
                    {errors.subcategory}
                  </p>
                )}
              </div>
            )}
      </Row>

      <Row label={translations.responsible}>
        {!editing || !canEdit ? (
          unlessLoading(
            <PlainLink
              to={responsible ? `/users/${responsible?.id}` : '#'}
              $hoverColor="secondary"
            >
              {responsible?.fullName ?? '-'}
            </PlainLink>
          )
        ) : (
          <UserCombobox
            mode="single"
            value={temporaryResponsible?.id ?? ''}
            dropdownPageName="issue_category_responsible"
            clearable={
              temporaryResponsible?.id !== category?.responsibleUser?.id
            }
            onChange={value => {
              onChange('responsibleUser')(value || null)
              if (!value)
                setTemporaryResponsible(category?.responsibleUser ?? null)
              else setTemporaryResponsible({ id: value })
            }}
          />
        )}
      </Row>

      <Row label={translations.caseHandler}>
        {!editing || !canEdit ? (
          unlessLoading(
            <PlainLink
              to={caseHandler ? `/users/${caseHandler?.id}` : '#'}
              $hoverColor="secondary"
            >
              {caseHandler?.fullName ?? '-'}
            </PlainLink>
          )
        ) : (
          <UserCombobox
            mode="single"
            value={form.caseHandler ?? ''}
            dropdownPageName="issue_category_responsible"
            onChange={value => onChange('caseHandler')(value || null)}
          />
        )}
      </Row>

      <Row
        label={translations.riskAspect}
        required={showEditMark && requireRiskAspect}
      >
        {!editing || !canEdit ? (
          unlessLoading(riskAspect?.name ?? '-')
        ) : (
          <IssueRiskAspectCombobox
            value={form.riskAspect}
            error={errors.riskAspect}
            onChange={value => onChange('riskAspect')(value)}
          />
        )}
      </Row>

      {processField.hasProcesses && (
        <Row label={translations.process}>
          {!editing || !canEdit ? (
            unlessLoading(processField.selectedLabel)
          ) : (
            <Combobox<string>
              mode="single"
              value={form.processChoice ?? ''}
              items={processField.options}
              clearable
              onChange={value => onChange('processChoice')(value || null)}
            />
          )}
        </Row>
      )}

      <Row label={translations.user}>
        {unlessLoading(
          <PlainLink
            to={user ? `/users/${user?.id}` : '#'}
            $hoverColor="secondary"
          >
            {user?.fullName ?? '-'}
          </PlainLink>
        )}
      </Row>

      <Row label={translations.reference()}>{unlessLoading(reference)}</Row>

      <Row label={translations.date}>
        {!editing ? (
          <span
            title={
              date ? format(new Date(date), 'dd.MM.yyyy HH:mm') : undefined
            }
          >
            {isSingleDay(new Date(date ?? 0), new Date(dateEnd ?? 0)) ? (
              unlessLoading(format(new Date(date ?? 0), 'PP, HH:mm'))
            ) : (
              <>
                {unlessLoading(format(new Date(date ?? 0), 'PP, HH:mm'))}
                {' – '}
                {unlessLoading(format(new Date(dateEnd ?? 0), 'PP, HH:mm'))}
              </>
            )}
          </span>
        ) : (
          <IssueDateRangeField
            start={form.date}
            end={form.dateEnd}
            onChange={(date, dateEnd) => updateForm({ date, dateEnd })}
          />
        )}
      </Row>
    </Card>
  )
}
